<script setup>
import { Badge } from '@drama/ui'

import AuditId from './AuditId.vue'
import { clockTime, fullTime, useAuditText, variantOf } from './audit-text.js'
import { asArray, characterName } from './run-observer.js'

const props = defineProps({
  pack: { type: Object, required: true },
  // 轮头标题：开场或"第 N 回合"。
  label: { type: String, required: true },
  segments: { type: Array, required: true },
  // 开场没有回合与请求，两者为 null。
  turn: { type: Object, default: null },
  request: { type: Object, default: null },
})

const { tr, enumLabel } = useAuditText()

function speakerName(segment) {
  if (!segment.speaker_id || segment.speaker_id === 'narrator') return tr('story.narrator')
  return characterName(props.pack, segment.speaker_id)
}

/** 见闻者放进 title：同一信息在人物页签的见闻表里逐条列出。 */
function audienceTitle(segment) {
  const names = asArray(segment.audience_ids).map((id) => characterName(props.pack, id))
  return names.length ? tr('story.audience', { names: names.join(tr('separator')) }) : undefined
}
</script>

<template>
  <article class="au-turn">
    <header class="au-turn__head">
      <strong>{{ label }}</strong>
      <template v-if="turn">
        <span>· {{ enumLabel('turns.modes', turn.input_mode) }}</span>
        <span v-if="request?.model">· {{ request.model }}</span>
        <span v-if="request?.charged_points">· {{ tr('points', { points: request.charged_points }) }}</span>
        <span v-if="request?.completed_at" :title="fullTime(request.completed_at)">· {{ clockTime(request.completed_at) }}</span>
        <Badge v-if="turn.recording" size="sm" :variant="variantOf(turn.recording)">{{ enumLabel('turns.recordingStates', turn.recording) }}</Badge>
        <AuditId v-if="turn.request_id" :id="turn.request_id" />
      </template>
    </header>
    <p v-if="request?.submitted_input" class="au-line is-player">
      <span class="au-line__who">{{ characterName(pack, pack.player_id) }}<small>{{ tr('story.playerInput') }}</small></span>
      <span class="au-line__text">{{ request.submitted_input.text || enumLabel('turns.modes', request.submitted_input.mode) }}</span>
    </p>
    <p
      v-for="(segment, index) in segments"
      :key="segment.segment_id || index"
      class="au-line"
      :class="{ 'is-player': segment.speaker_id === pack.player_id }"
      :title="audienceTitle(segment)"
    >
      <span class="au-line__who">{{ speakerName(segment) }}<small>{{ enumLabel('story.channels', segment.channel) }}</small></span>
      <span class="au-line__text">{{ segment.text }}</span>
      <Badge v-if="segment.metadata_pending" size="sm" variant="warning">{{ tr('story.metadataPending') }}</Badge>
    </p>
  </article>
</template>
